@let integrationName = serviceIdentifier | integrationNameKey | translate;
@let reauthTooltipText = "int_invoice_reauth_required_tooltip" | translate: { integrationName };

<p-dialog
    header="{{ 'int_invoice_modal_header' | translate: { integrationName } }}"
    [(visible)]="showInvoiceDialog"
    [modal]="true"
    [draggable]="true"
    [resizable]="false"
    [style]="{ width: '550px' }"
    (onHide)="onCancelInvoice()"
>
    <div class="p-fluid">
        <!-- Reauthentication Warning Message -->
        <!-- TODO: Check if this can be removed as the button to open the dialog should be disabled when in reauth state -->
        @if (needsReauth) {
            <p-message severity="warn">
                <div class="flex flex-col">
                    <span class="p-message-summary text-md font-bold">{{ reauthTooltipText }}</span>
                    <a [routerLink]="['/settings/integrations']" [queryParams]="{ needsReauth: 'true' }">
                        {{ "nav_settings" | translate }}
                    </a>
                </div>
            </p-message>
        }

        <!-- Date Range -->
        <div class="field mb-4">
            <div class="flex justify-between items-center flex-wrap mb-4">
                <label for="InvoicePeriod" class="font-bold">{{ "int_invoice_period_label" | translate }}</label>
                <span class="text-nowrap">{{ invoiceStartDate }} - {{ invoiceEndDate }}</span>
            </div>
        </div>

        <p-divider></p-divider>

        <!-- Total Time -->
        <div class="field mb-4">
            <div class="field flex justify-between items-center flex-wrap">
                <label for="invoiceTotalTime" class="mr-2 font-bold">{{ "int_invoice_totalTime_label" | translate }}</label>
                <span class="font-bold">{{ invoiceTotalTime }}</span>
            </div>
        </div>

        <p-divider></p-divider>

        <!-- Customer Selection -->
        <div class="field mb-4">
            <div class="field mb-4 flex justify-between items-center">
                <label for="Contact" class="font-bold">
                    {{ "int_invoice_contacts_dropdown_label" | translate }}
                    <span>*</span>
                </label>
                <div class="flex items-center">
                    <!-- Show dropdown only when not loading -->
                    <p-select
                        data-testid="invoice-contact"
                        class="w-80"
                        [disabled]="needsReauth"
                        [filter]="true"
                        id="Contact"
                        [dropdownIcon]="isLoadingContacts ? 'pi pi-spin pi-spinner' : 'pi pi-chevron-down'"
                        [options]="contactDropdownOptions"
                        [(ngModel)]="selectedContact"
                        optionLabel="label"
                        [placeholder]="needsReauth ? reauthTooltipText : ('int_customer_dropdown_placeholder' | translate: { integrationName })"
                        (onChange)="onContactChange($event)"
                    >
                        <ng-template #selectedItem let-option>
                            <div class="contact-option">
                                <span>{{ option?.label }}</span>
                                @if (option.value === autoSelectContact?.value) {
                                    <p-tag value="{{ 'suggested_tag' | translate }}" icon="pi pi-sparkles" severity="info" class="suggested-tag" />
                                }
                            </div>
                        </ng-template>
                        <ng-template #item let-option>
                            <div class="contact-option">
                                <span>{{ option.label }}</span>
                                @if (option.value === autoSelectContact?.value) {
                                    <p-tag value="{{ 'suggested_tag' | translate }}" icon="pi pi-sparkles" severity="info" class="suggested-tag" />
                                }
                            </div>
                        </ng-template>
                    </p-select>
                    <button
                        pButton
                        severity="secondary"
                        type="button"
                        icon="pi pi-refresh"
                        class="ml-2 p-button-text whitespace-nowrap"
                        pTooltip="{{ 'int_refresh_list_button_tooltip' | translate }}"
                        [disabled]="isLoadingContacts || needsReauth"
                        (click)="fetchContacts()"
                    ></button>
                </div>
            </div>

            @if (selectedContactObj?.flags?.noAddr) {
                <p-message severity="warn" class="message-warn-orange" [escape]="false">
                    <span>
                        {{ "int_no_address_for_contact" | translate }}
                        <!-- eslint-disable-next-line @html-eslint/no-ineffective-attrs -->
                        <a [href]="selectedContactObj?.link" target="_blank" class="warn-orange underline font-bold ml-1">
                            {{ "add_address" | translate }}
                        </a>
                    </span>
                </p-message>
            }
        </div>

        <p-divider></p-divider>

        <!-- Activity Grouping -->
        <div class="field mb-4">
            <label for="GroupingOption" class="font-bold block mb-2"> {{ "int_invoice_grouping_dropdown_label" | translate }} <span>*</span></label>
            <div id="GroupingOption">
                @for (option of groupingOptions; track option.value) {
                    <div class="flex items-center mb-2">
                        <p-radioButton
                            [attr.data-testid]="'invoice-grouping-' + option.value"
                            name="GroupingOption"
                            [value]="option"
                            [(ngModel)]="selectedGroupingOption"
                            [disabled]="needsReauth"
                            inputId="grouping-{{ option.value }}"
                            (ngModelChange)="onGroupingOptionChange($event)"
                        >
                        </p-radioButton>
                        <label [for]="'grouping-' + option.value" class="ml-2">{{ option.label }}</label>
                    </div>
                }
            </div>
            @if (missingUnits && missingUnits.length > 0) {
                <div class="mt-4">
                    <p-message severity="warn" class="message-warn-orange" [escape]="false">
                        <span>
                            {{ "int_invoice_missing_units_text" | translate }} "{{ missingUnits.join(", ") }}".
                            @if (isAdmin) {
                                <a (click)="openManageUnits()" class="cursor-pointer underline font-bold ml-1 warn-orange">
                                    {{ "int_invoice_manage_units_link" | translate }}
                                </a>
                            }
                        </span>
                    </p-message>
                </div>
            }

            <!-- notes -->
            <div class="field mt-4">
                <label for="includeNotesCheckbox" class="font-bold mb-2 block"> {{ "integration_invoice_pos_details_label" | translate }} </label>
                <div class="flex items-center">
                    <p-checkbox
                        data-testid="invoice-include-notes"
                        name="includeNotes"
                        [(ngModel)]="includeNotes"
                        [binary]="true"
                        inputId="includeNotesCheckbox"
                        [disabled]="needsReauth"
                        class="mr-2"
                    >
                    </p-checkbox>
                    <label for="includeNotesCheckbox" class="font-normal mb-0 cursor-pointer">
                        {{ "integration_invoice_pos_details_notes" | translate }}
                    </label>
                </div>
            </div>
        </div>

        <p-divider></p-divider>

        <!-- Hourly rate -->
        <div class="p-field p-grid p-align-center">
            <label for="amount" class="font-bold">
                {{ "int_hourly_rate_label" | translate }}
            </label>
            <div class="p-col mt-2">
                <p-inputgroup class="w-36!">
                    <p-inputgroup-addon class="bg-gray-200! text-gray-700! dark:bg-transparent! dark:text-gray-500!"> € </p-inputgroup-addon>
                    <p-inputNumber
                        data-testid="invoice-hourly-rate"
                        id="amount"
                        [(ngModel)]="hourlyRate"
                        [min]="0"
                        [mode]="'decimal'"
                        [useGrouping]="false"
                        [placeholder]="'0.00'"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="2"
                        [locale]="currentUserLocale"
                    />
                </p-inputgroup>
                <div class="mt-4 tb-text-gray">{{ "int_invoice_hourly_rate_info" | translate }}</div>
            </div>
        </div>

        <p-divider></p-divider>

        <!-- Optional: mark all activities in invoice as billed -->
        <div class="field mt-4">
            <label for="markBilled" class="font-bold mb-2 block"> {{ "int_mark_billed_header" | translate }} </label>
            <div class="flex items-center">
                <p-checkbox
                    data-testid="invoice-mark-billed"
                    name="markBilled"
                    [(ngModel)]="markBilled"
                    [binary]="true"
                    inputId="markBilled"
                    [disabled]="needsReauth"
                    class="mr-2"
                >
                </p-checkbox>
                <label for="markBilled" class="font-normal mb-0 cursor-pointer"> {{ "int_mark_billed_label" | translate }} </label>
            </div>
        </div>
    </div>

    <ng-template pTemplate="footer">
        <div class="flex mt-6 justify-end" style="width: 58%; margin-left: auto">
            <button
                type="submit"
                severity="secondary"
                pButton
                (click)="onCancelInvoice()"
                class="p-button-text whitespace-nowrap mr-2"
                [disabled]="isInvoiceLoading"
            >
                {{ "button_cancel" | translate }}
            </button>
            <p-button
                data-testid="invoice-submit"
                [loading]="isInvoiceLoading"
                [disabled]="isInvoiceSaveDisabled"
                (click)="saveInvoiceSettings()"
                styleClass="whitespace-nowrap"
                label="{{ 'int_invoice_modal_submit_label' | translate }}"
            >
            </p-button>
        </div>
    </ng-template>
</p-dialog>

<p-toast key="invoice">
    <ng-template let-message pTemplate="headless">
        <div class="flex items-center w-full m-2">
            <!-- Success Icon -->
            <i class="pi pi-check-circle p-toast-icon text-2xl ml-4"></i>
            <!-- Message Content -->
            <div class="flex-1 ml-4">
                <div class="font-bold">{{ message.summary | translate }}</div>
                <div class="font-bold mt-2">
                    @let dtid = "invoice-success-link-" + serviceIdentifier;
                    <!-- eslint-disable-next-line @html-eslint/no-ineffective-attrs -->
                    <a [attr.data-testid]="dtid" class="p-link font-bold text-green-700 dark:text-green-200 underline" [href]="message.detail" target="_blank">
                        {{ "view_invoice" | translate }}
                        <i class="pi pi-external-link ml-2"></i>
                    </a>
                </div>
            </div>
            <!-- Close Icon -->
            <button type="button" pButton class="p-button-text text-inherit! p-component mr-2 -mt-6" (click)="messageService.clear(message.key || 'invoice')">
                <i class="pi pi-times"></i>
            </button>
        </div>
    </ng-template>
</p-toast>
